import { useState } from "react";
import { Link } from "react-router-dom";
import { ArticleMedia } from "../V2Shared";
import { ageMinutes } from "../tokens";
import { H3, h3Age } from "./h3";

/* Breaking Now — Figma 214:20 (1440 windowed cards + controls), 205:10-21
   (768 static top-3, no card borders, no controls), 206:9 handled by
   BreakingMobile. Real breaking queue only. */
const isFresh = (a: any) => (ageMinutes(a.breaking_at || a.published_at) ?? 99) <= 10;

export const BreakingCarousel = ({ items }: { items: any[] }) => {
  const [off, setOff] = useState(0);
  if (!items.length) return null;
  const n = items.length;
  const win = (vis: number) => Array.from({ length: Math.min(vis, n) }, (_, i) => items[(off + i) % n]);

  const Ctl = ({ dir, testid }: { dir: -1 | 1; testid: string }) => (
    <button type="button" data-testid={testid} aria-label={dir === 1 ? "Next breaking stories" : "Previous breaking stories"}
      onClick={() => setOff((o) => (o + dir + n) % n)}
      className="w-[28px] h-[28px] rounded-[5px] shrink-0 flex items-center justify-center text-[18px] leading-none transition-colors hover:border-[#6e33ff]"
      style={{ background: "#06090e", border: `1px solid ${H3.border}`, color: H3.text }}>
      {dir === 1 ? "›" : "‹"}
    </button>
  );

  return (
    <div data-testid="h3-breaking-carousel"
      className="hidden md:flex rounded-[6px] items-center gap-[10px] xl:gap-[12px] px-[14px] py-[10px] md:min-h-[96px] xl:min-h-[93px]"
      style={{ background: H3.card, border: `1px solid ${H3.border}` }}>
      <span className="nl-geist shrink-0 text-[10px] md:text-[11px] xl:text-[10px] leading-[13px] font-bold rounded-[4px] md:rounded-[6px] xl:rounded-[4px] px-[10px] md:px-[12px] xl:px-[10px] py-[6px] md:py-[8px] xl:py-[6px] whitespace-nowrap"
        style={{ background: H3.redBg, border: `1px solid ${H3.red}`, color: H3.text }}
        data-testid="h3-breaking-badge">
        BREAKING NOW
      </span>

      {/* 768: static top-3, plain rows (205:13-21) */}
      <div className="flex xl:hidden min-w-0 flex-1 gap-[14px] items-center">
        {win(3).map((a, i) => {
          const fresh = i === 0 && isFresh(a);
          return (
            <Link key={a.id} to={`/article/${a.slug}`} data-testid={`h3-breaking-md-${i + 1}`}
              className="group min-w-0 flex-1 flex gap-[10px] items-start hover:opacity-90 transition-opacity">
              <span className="nl-mono text-[11px] leading-[15px] font-semibold shrink-0 pt-[1px]"
                style={{ color: fresh ? H3.redTouch : "#808ca6" }}>
                {String((off + i) % n + 1).padStart(2, "0")}
              </span>
              <ArticleMedia article={a} className="w-[46px] h-[46px] rounded-[7px] shrink-0" />
              <div className="min-w-0">
                <p className="nl-geist text-[12px] leading-[15px] font-semibold line-clamp-2 group-hover:text-white transition-colors"
                  style={{ color: H3.text }}>
                  {a.title}
                </p>
                <span className="nl-mono block mt-[4px] text-[10px] leading-[13px]"
                  style={{ color: fresh ? H3.redTouch : "#808ca6" }}>
                  {h3Age(a.breaking_at || a.published_at)}
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      {/* 1440/1280: windowed story cards ×4 + controls (214:23-38, 217:2-4) */}
      <div className="hidden xl:flex min-w-0 flex-1 gap-[8px] items-center">
        {win(4).map((a, i) => {
          const fresh = i === 0 && isFresh(a);
          return (
            <Link key={a.id} to={`/article/${a.slug}`} data-testid={`h3-breaking-card-${i + 1}`}
              className="group min-w-0 flex-1 rounded-[6px] px-[10px] py-[8px] flex gap-[8px] items-start hover:opacity-90 transition-opacity"
              style={{ background: H3.card, border: fresh ? `1.5px solid ${H3.red}` : `1px solid ${H3.border}` }}>
              <span className="nl-geist text-[15px] leading-[19px] shrink-0" style={{ color: H3.text }}>
                {((off + i) % n) + 1}
              </span>
              <ArticleMedia article={a} className="w-[58px] h-[52px] rounded-[4px] shrink-0" />
              <div className="min-w-0">
                <p className="nl-geist text-[10px] leading-[13px] font-medium line-clamp-3 group-hover:text-white transition-colors"
                  style={{ color: H3.text }}>
                  {a.title}
                </p>
                <span className="nl-mono block mt-[2px] text-[9px] leading-[13px]"
                  style={{ color: fresh ? H3.red : H3.gray }}>
                  {h3Age(a.breaking_at || a.published_at)}
                </span>
              </div>
            </Link>
          );
        })}
        {n > 4 && (
          <div className="flex gap-[6px] shrink-0">
            <Ctl dir={-1} testid="h3-breaking-prev" /><Ctl dir={1} testid="h3-breaking-next" />
          </div>
        )}
      </div>
    </div>
  );
};

/* 390-native breaking block (206:6-16): badge + count, story + thumb, next teaser */
export const BreakingMobile = ({ items }: { items: any[] }) => {
  const [off, setOff] = useState(0);
  if (!items.length) return null;
  const n = items.length;
  const cur = items[off % n];
  const next = items[(off + 1) % n];
  const fresh = isFresh(cur);
  return (
    <div data-testid="h3-breaking-mobile" className="md:hidden rounded-[10px] p-[14px]"
      style={{ background: "#020305", border: `1px solid ${H3.borderTouch}` }}>
      <div className="flex items-center justify-between">
        <span className="nl-geist text-[10px] leading-[13px] font-semibold rounded-[6px] px-[10px] py-[7px]"
          style={{ background: H3.redBgTouch, border: `1px solid ${H3.redTouch}`, color: H3.text }}>
          BREAKING NOW
        </span>
        <span className="nl-mono text-[10px] leading-[14px]" style={{ color: H3.grayTouch }} data-testid="h3-breaking-count">
          {(off % n) + 1} / {n}
        </span>
      </div>
      <div className="flex items-start gap-[10px] mt-[12px]">
        <span className="nl-mono text-[11px] leading-[15px] font-semibold shrink-0 pt-[2px]"
          style={{ color: fresh ? H3.redTouch : H3.grayTouch }}>
          {String((off % n) + 1).padStart(2, "0")}
        </span>
        <div className="min-w-0 flex-1">
          <Link to={`/article/${cur.slug}`} data-testid="h3-breaking-mobile-link"
            className="nl-geist block text-[14px] leading-[17px] font-semibold line-clamp-3 hover:opacity-80 transition-opacity"
            style={{ color: H3.text }}>
            {cur.title}
          </Link>
          <span className="nl-mono block mt-[5px] text-[10px] leading-[13px]"
            style={{ color: fresh ? H3.redTouch : H3.grayTouch }}>
            {h3Age(cur.breaking_at || cur.published_at)}
          </span>
        </div>
        <ArticleMedia article={cur} className="w-[46px] h-[46px] rounded-[7px] shrink-0" />
      </div>
      {n > 1 && (
        <div className="flex items-center gap-[10px] mt-[10px] pt-[10px] border-t" style={{ borderColor: H3.borderTouch }}>
          <span className="nl-mono text-[10px] leading-[14px] font-semibold shrink-0" style={{ color: H3.violetTouch }}>
            {String(((off + 1) % n) + 1).padStart(2, "0")}
          </span>
          <p className="nl-geist text-[12px] leading-[16px] font-medium truncate flex-1" style={{ color: H3.text }}>{next.title}</p>
          <button type="button" aria-label="Next breaking story" data-testid="h3-breaking-next-btn"
            onClick={() => setOff((o) => (o + 1) % n)}
            className="nl-geist shrink-0 w-[36px] h-[36px] -my-[6px] rounded-[6px] flex items-center justify-center text-[15px] hover:opacity-80 transition-opacity"
            style={{ border: `1px solid ${H3.borderTouch}`, color: H3.violetTouch }}>
            →
          </button>
        </div>
      )}
    </div>
  );
};
